import { formatOwnValue } from "../../shared/seriesOwnValues";

export { formatDateLong } from "../../shared/seriesOwnValues";
import { offsetParts } from "./components/ShowDetailOffset";
import { SERIES_SPONSOR_LOGO_KEY } from "./seriesSettings";
import { LOCATION_PRIVACY_OPTIONS } from "../shared/constants/locationPrivacyOptions";

export const plural = (count, one, many) => (count === 1 ? one : many || `${one}s`);

// "A", "A and B", "A, B and C".
export const listText = (items) =>
  (items.length <= 1 ? items.join("") : `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`);

const TEXT_LIMIT = 42;
const truncate = (text) => (text.length > TEXT_LIMIT ? `${text.slice(0, TEXT_LIMIT - 1)}…` : text);

export const venueDay = (iso, timeZone) => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    ...(timeZone ? { timeZone } : {}),
  }).formatToParts(new Date(iso));
  const part = (type) => parts.find((p) => p.type === type).value;
  return `${part("year")}-${part("month")}-${part("day")}`;
};

export const ruleText = (rule, timeZone) => {
  if (!rule) return "—";
  if (rule.offset_minutes !== null && rule.offset_minutes !== undefined) {
    const { amount, unit, direction } = offsetParts(rule.offset_minutes);
    if (!amount) return "Each date's start";
    return `${amount} ${Number(amount) === 1 ? unit.slice(0, -1) : unit} ${direction}`;
  }
  if (rule.start_time) return `Fixed: ${formatOwnValue(rule.start_time, "time", { timeZone })}`;
  return "Not set";
};

const discountText = (discount, currency) => {
  if (!discount) return "—";
  if (discount.percentage) return `${Number(discount.percentage)}% off`;
  if (discount.amount) return `${formatOwnValue(discount.amount, "money", { currency })} off`;
  return "—";
};

const parsed = (value) => {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const countOf = (value) => {
  const list = parsed(value);
  return Array.isArray(list) ? list.length : 0;
};

// A charge row's enabled flag can arrive as a boolean or as its string form.
const enabledCountOf = (value) => {
  const list = parsed(value);
  return Array.isArray(list) ? list.filter((row) => row && row.enabled !== false && row.enabled !== "false").length : 0;
};

const logoText = (value, side) => {
  if (side === "after") return value ? "New logo" : "Logo removed";
  return value ? "Logo" : "No logo";
};

const NAMED_FIELDS = ["room_ids", "confirmation_page_template_ids", "general_ledger_code_id", "location_reveal_offset_minutes"];
const TEAM_DEFAULT_FIELDS = ["collect_waitlist_interest_enabled", "tixel_waitlist_redirect_enabled"];

const confirmationText = (value, side) => {
  if (side === "after") return value ? "Edited text" : "Text removed";
  return value ? "Custom text" : "No custom text";
};

const SPONSOR_FIELDS = ["show_sponsor_on_digital_tickets", "sponsor_name", "sponsor_logo"];
const SERIES_DEFAULT = "Series default";
const isNull = (value) => value === null || value === undefined;

const settingSummary = (values, side, names = {}, kind = "setting") => {
  const entries = Object.entries(values || {});
  if (entries.length === 0) return "—";
  // A show type's null copy or sponsor uses the series' own, so it reads as
  // the series default; a null sponsor switch stands for the whole sponsor.
  const showType = kind === "show_type_settings";
  const followsSponsor = showType && "show_sponsor_on_digital_tickets" in values && isNull(values.show_sponsor_on_digital_tickets);
  const pixels = entries.filter(([field]) => field.endsWith("_marketing_pixels"));
  const others = entries.filter(([field]) => !field.endsWith("_marketing_pixels"));
  const parts = others.map(([field, value]) => {
    if (followsSponsor && SPONSOR_FIELDS.includes(field)) return SERIES_DEFAULT;
    if (showType && field === "ticket_confirmation_info" && isNull(value)) return SERIES_DEFAULT;
    if (field === "confirmation_page_template_ids" && isNull(value)) return "Team default";
    if (field === "is_public") return value === "true" ? "Public" : "Private";
    if (field === "sponsor_logo") return logoText(value, side);
    if (field === "organizer_fees") {
      const fees = parsed(value) || {};
      const count = (fees.components || []).length + (fees.overrides || []).length;
      return `${count} ${plural(count, "fee")}`;
    }
    if (field === "planned_custom_charges") return `${enabledCountOf(value)} on`;
    if (field === "planned_agreements") return `${countOf(value)} ${plural(countOf(value), "agreement")}`;
    if (field === "ticket_confirmation_info") return confirmationText(value, side);
    if (field === "location_privacy") return optionText(LOCATION_PRIVACY_OPTIONS, value);
    if (TEAM_DEFAULT_FIELDS.includes(field) && (value === null || value === undefined)) return "Team default";
    if (names[field]) return names[field];
    if (NAMED_FIELDS.includes(field)) return value === null || value === undefined ? "None" : "—";
    return truncate(formatOwnValue(value, "setting"));
  });
  if (pixels.length > 0) {
    const count = pixels.reduce((sum, [, value]) => sum + countOf(value), 0);
    parts.push(`${count} ${plural(count, "pixel")}`);
  }
  return parts.filter((part, index) => part !== SERIES_DEFAULT || parts.indexOf(part) === index).join(", ");
};

const textOf = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  return truncate(typeof value === "object" ? formatOwnValue(JSON.stringify(value), "setting") : String(value));
};

const optionText = (options, value) => {
  if (value === null || value === undefined || value === "") return "—";
  const option = (options || []).find((entry) => String(entry.value) === String(value));
  return option ? truncate(String(option.label)) : "—";
};

// One side (before or after) of a change, as the change list shows it.
export const formatChangeValue = (change, side, { preview, currency, timeZone, eventTypes, eventTemplates } = {}) => {
  const value = change[side];
  switch (change.kind) {
    case "on_sale":
    case "time":
      return ruleText(value, timeZone);
    case "chart": {
      const chart = ((preview && preview.chart) || []).find((entry) => entry.change_id === change.id);
      if (chart) return side === "before" ? chart.from.name : chart.to.name;
      return textOf(value);
    }
    case "price":
      return value === null || value === undefined ? "—" : formatOwnValue(value, "money", { currency });
    case "quantity":
      return formatOwnValue(value, "quantity");
    case "discount":
      return discountText(value, currency);
    case "setting":
    case "show_type_settings":
      return settingSummary(value, side, (change.display || {})[side], change.kind);
    case "image":
      if (side === "after") return value ? "New image" : "Image removed";
      return value ? "Image" : "No image";
    case "photo":
      // A removed photo saves with the series, never through the preview.
      if (side === "after") return "New photo";
      return value ? "Photo" : "No photo";
    case "event_type":
      return optionText(eventTypes, value);
    case "event_template":
      return optionText(eventTemplates, value);
    case "structure":
      return Array.isArray(value) ? truncate(value.join(", ")) || "—" : textOf(value);
    default:
      return textOf(value);
  }
};

const rowName = (change, changes) => {
  const rename = changes.find((other) => other.row && other.row === change.row && (other.kind === "rename" || other.kind === "name"));
  if (rename) return rename.after;
  return (change.label || "").replace(/ (price|quantity|description|discount)$/, "");
};

const SALE_SETTINGS = ["Organizer fees", "Custom charges", "Statement descriptor"];

// Outcomes with their own note, or none needed (the chart panel and the own
// values alert speak for kept_chart and kept_own_value).
const KNOWN_OUTCOMES = [
  "updated", "removed", "hidden", "renamed", "swapped", "built", "not_on_date", "checkout_gate_failed",
  "kept_quantity", "kept_times", "kept_on_sale", "kept_chart", "kept_own_value", "kept_revealed", "updated_keeps_date_rows",
  "kept_sold", "kept_own_fees", "kept_fees_in_use", "name_in_use",
];

// Builds the "What happens to tickets" and "What happens to add-ons and promo
// codes" notes from the preview's per-change counts. Each note is
// { key, tone: "info" | "warning" | "error", parts } where parts alternate
// between plain text and { strong } runs.
export const updateNotes = (preview) => {
  const changes = (preview && preview.changes) || [];
  const notes = (preview && preview.notes) || {};
  const noteFor = (change) => notes[change.id] || { dates: 0, outcomes: {}, dates_with_sales: 0, sold: 0, on_sale_now: 0, moved_later_on_live_dates: 0 };
  const outcome = (change, name) => (noteFor(change).outcomes || {})[name] || 0;
  const tickets = [];
  const extras = [];
  const settings = [];
  const pushable = changes.filter((change) => change.pushable);
  const upcoming = (preview && preview.summary && preview.summary.upcoming_dates) || 0;
  const everyDate = (change) => (noteFor(change).dates >= upcoming ? "every date" : "these dates");

  const ticketPrices = pushable.filter((change) => change.group === "ticket_types" && change.kind === "price");
  const withSales = Math.max(0, ...ticketPrices.map((change) => noteFor(change).dates_with_sales));
  if (withSales > 0) {
    tickets.push({
      key: "ticket-prices",
      tone: "info",
      parts: [`${withSales} of these dates ${plural(withSales, "has", "have")} sales. The new price applies to future orders; tickets already sold keep the price they paid.`],
    });
  }

  const saleSettings = pushable.filter((change) => change.group === "settings" && SALE_SETTINGS.includes(change.label));
  if (saleSettings.length > 0) {
    tickets.push({
      key: "sale-settings",
      tone: "info",
      parts: [`${listText(saleSettings.map((change) => change.label))} ${saleSettings.length === 1 ? "applies" : "apply"} to future orders. Orders already placed aren't changed.`],
    });
  }

  const accountCode = pushable.find((change) => change.group === "settings" && (change.fields || []).includes("general_ledger_code_id"));
  const soldDates = accountCode
    ? ((preview && preview.dates) || []).filter((date) => date.included && date.has_sales && (date.changes || {})[accountCode.id]).length
    : 0;
  if (soldDates > 0) {
    tickets.push({ key: "account-code", tone: "info", parts: ["Sales already made on these dates will report under the new account code."] });
  }

  pushable.filter((change) => change.group === "ticket_types" && change.kind === "removed").forEach((change) => {
    const hidden = outcome(change, "hidden");
    tickets.push({
      key: change.id,
      tone: "info",
      parts: hidden > 0
        ? [{ strong: change.before }, ` is hidden on the ${hidden} ${plural(hidden, "date")} with sales instead of being deleted, and removed from the rest.`]
        : [{ strong: change.before }, ` is removed from ${everyDate(change)}.`],
    });
  });

  pushable.filter((change) => change.kind === "on_sale").forEach((change) => {
    const { on_sale_now: live, moved_later_on_live_dates: later } = noteFor(change);
    if (live > 0) {
      tickets.push({
        key: `${change.id}:now`,
        tone: "warning",
        parts: [`The new on-sale time has already passed for ${live} ${plural(live, "date")}, so ${live === 1 ? "it goes" : "they go"} on sale as soon as you save.`],
      });
    }
    if (later > 0) {
      tickets.push({
        key: `${change.id}:later`,
        tone: "info",
        parts: [`${later} ${plural(later, "date is", "dates are")} already on sale, so ${later === 1 ? "it keeps its" : "they keep their"} on-sale time. Moving on-sale later doesn't take ${later === 1 ? "it" : "them"} off sale.`],
      });
    }
  });

  const keptQuantity = Math.max(0, ...pushable
    .filter((change) => change.group === "ticket_types" && change.kind === "quantity")
    .map((change) => outcome(change, "kept_quantity")));
  if (keptQuantity > 0) {
    tickets.push({
      key: "ticket-quantity",
      tone: "error",
      parts: [`${keptQuantity} ${plural(keptQuantity, "date has", "dates have")} already sold more than the new quantity, so ${keptQuantity === 1 ? "it keeps its" : "they keep their"} current quantity.`],
    });
  }

  const keptTimes = Math.max(0, ...pushable.filter((change) => change.kind === "time").map((change) => outcome(change, "kept_times")));
  if (keptTimes > 0) {
    tickets.push({
      key: "kept-times",
      tone: "info",
      parts: [`${keptTimes} ${plural(keptTimes, "date with sales keeps its", "dates with sales keep their")} times.`],
    });
  }

  pushable.filter((change) => change.group === "add_ons").forEach((change) => {
    const note = noteFor(change);
    const name = rowName(change, changes);
    if (change.kind === "price") {
      extras.push(note.sold > 0
        ? { key: change.id, tone: "info", parts: [{ strong: name }, ` has sold ${note.sold.toLocaleString()} on ${note.dates_with_sales} of these dates. The new price applies to future orders; those buyers keep the price they paid.`] }
        : { key: change.id, tone: "info", parts: [{ strong: name }, " hasn't sold on these dates yet, so every buyer gets the new price."] });
    } else if (change.kind === "quantity" && outcome(change, "kept_quantity") > 0) {
      const kept = outcome(change, "kept_quantity");
      extras.push({ key: change.id, tone: "error", parts: [`${kept} ${plural(kept, "date has", "dates have")} already sold more `, { strong: name }, ` than the new quantity, so ${kept === 1 ? "it keeps its" : "they keep their"} current quantity.`] });
    } else if (change.kind === "removed") {
      const hidden = outcome(change, "hidden");
      extras.push(hidden > 0
        ? { key: change.id, tone: "info", parts: [{ strong: change.before }, ` is hidden on the ${hidden} ${plural(hidden, "date")} where it has sold, instead of being deleted, and removed from the rest. Buyers keep what they bought.`] }
        : { key: change.id, tone: "info", parts: [{ strong: change.before }, ` is removed from ${everyDate(change)}.`] });
    } else if (change.kind === "new") {
      extras.push({ key: change.id, tone: "info", parts: [{ strong: change.after }, ` is added to each of the ${note.dates} ${plural(note.dates, "date")}, with its own quantity on each.`] });
    }
  });

  pushable.filter((change) => change.group === "promo_codes").forEach((change) => {
    const note = noteFor(change);
    const inUse = outcome(change, "name_in_use");
    if (inUse > 0) {
      const code = change.kind === "new" || change.kind === "rename" ? change.after : rowName(change, changes);
      extras.push({
        key: `${change.id}:in-use`,
        tone: "warning",
        parts: [`${inUse} ${plural(inUse, "date already has its", "dates already have their")} own active promo code named `, { strong: code }, `, so ${inUse === 1 ? "it keeps" : "they keep"} it.`],
      });
    }
    if (change.kind === "new") {
      const created = note.dates - inUse;
      if (created > 0) extras.push({ key: change.id, tone: "info", parts: [{ strong: change.after }, ` is created on each of the ${created} ${plural(created, "date")}, with its own use limit on each.`] });
    } else if (change.kind === "discount") {
      extras.push({
        key: change.id,
        tone: "info",
        parts: ["The new ", { strong: rowName(change, changes) }, ` discount applies to future orders.${note.sold > 0 ? ` The ${note.sold.toLocaleString()} ${plural(note.sold, "order")} that already used it keep the discount they got.` : ""}`],
      });
    } else if (change.kind === "rename") {
      extras.push({
        key: change.id,
        tone: "warning",
        parts: [{ strong: change.before }, " stops working on these dates and ", { strong: change.after }, ` takes its place. Anyone you've already sent ${change.before} to will need the new code.`],
      });
    } else if (change.kind === "removed") {
      extras.push({
        key: change.id,
        tone: "info",
        parts: [{ strong: change.before }, ` is turned off on ${note.dates} ${plural(note.dates, "date")}.${note.sold > 0 ? ` It's been used ${note.sold.toLocaleString()} ${plural(note.sold, "time")} on ${note.dates_with_sales} of them; those orders keep their discount.` : ""}`],
      });
    }
  });

  const settingChanges = pushable.filter((change) => change.kind === "setting" || change.kind === "show_type_settings");
  const mostOf = (name, filter = () => true) =>
    Math.max(0, ...settingChanges.filter(filter).map((change) => outcome(change, name)));

  const revealed = mostOf("kept_revealed");
  if (revealed > 0) {
    settings.push({
      key: "kept-revealed",
      tone: "info",
      parts: [`${revealed} ${plural(revealed, "date has", "dates have")} already revealed ${revealed === 1 ? "its" : "their"} location, so ${revealed === 1 ? "it keeps its" : "they keep their"} location settings.`],
    });
  }

  settingChanges.forEach((change) => {
    const kept = outcome(change, "updated_keeps_date_rows");
    if (kept === 0) return;
    const what = change.label.toLowerCase();
    settings.push({
      key: `${change.id}:date-rows`,
      tone: "info",
      parts: [kept === 1
        ? `1 date has ${what} it added itself. It keeps those alongside the series' ${what}.`
        : `${kept} dates have ${what} they added themselves. They keep those alongside the series' ${what}.`],
    });
  });

  pushable.filter((change) => change.kind === "event_template").forEach((change) => {
    const keptSold = outcome(change, "kept_sold");
    if (keptSold === 0) return;
    settings.push({
      key: `${change.id}:kept-sold`,
      tone: "info",
      parts: [`${keptSold} ${plural(keptSold, "date has", "dates have")} sales, so ${keptSold === 1 ? "it keeps its" : "they keep their"} current event template.`],
    });
  });

  const feesInUse = mostOf("kept_fees_in_use");
  if (feesInUse > 0) {
    settings.push({
      key: "kept-fees-in-use",
      tone: "info",
      parts: [`${feesInUse} ${plural(feesInUse, "date has tickets that already carry a fee the series removed, so it keeps", "dates have tickets that already carry a fee the series removed, so they keep")} that fee alongside the series' fees.`],
    });
  }

  const ownFees = mostOf("kept_own_fees");
  if (ownFees > 0) {
    settings.push({
      key: "kept-own-fees",
      tone: "info",
      parts: [`${ownFees} ${plural(ownFees, "date has its", "dates have their")} own organizer fees, so ${ownFees === 1 ? "it keeps" : "they keep"} them.`],
    });
  }

  // An outcome added on the server before this file knows it: a kept_* one
  // still says the dates keep their current value.
  pushable.forEach((change) => {
    Object.entries(noteFor(change).outcomes || {}).forEach(([name, count]) => {
      if (KNOWN_OUTCOMES.includes(name) || !name.startsWith("kept_") || !(count > 0)) return;
      const what = (change.label || "value").toLowerCase();
      const note = {
        key: `${change.id}:${name}`,
        tone: "info",
        parts: [`${count} ${plural(count, "date keeps its", "dates keep their")} current ${what}.`],
      };
      if (change.group === "ticket_types") tickets.push(note);
      else if (change.group === "add_ons" || change.group === "promo_codes") extras.push(note);
      else settings.push(note);
    });
  });

  return { tickets, extras, settings };
};

// The preview never takes files, so a new photo, sponsor logo or add-on image
// waiting to upload is listed from the editor's own state.
export const pendingFileRows = ({ photo = null, sponsorLogos = [], addOnImages = [] } = {}) => [
  ...(photo ? [{ key: "file:photo", label: "Photo", from: "", to: "New photo" }] : []),
  ...sponsorLogos.map((logo) => ({
    key: `file:logo:${logo.key}`,
    label: `Sponsor logo (${logo.key === SERIES_SPONSOR_LOGO_KEY ? "Series" : logo.key})`,
    from: "",
    to: "New logo",
  })),
  ...addOnImages.map((addOn) => ({
    key: `file:add-on:${addOn.clientKey}`,
    label: `${(addOn.name || "").trim() || "Add-on"} image`,
    from: "",
    to: "New image",
  })),
];

export const noteText = (note) => note.parts.map((part) => (typeof part === "string" ? part : part.strong)).join("");

// The date-update request: the edited series plus who gets it.
export const appendDateUpdateParams = (data, { scope, from, selectedIds, ownValues, chosen }) => {
  data.append("scope", scope);
  if (scope === "from" && from) data.append("from", from);
  if (scope === "selected") selectedIds.forEach((id) => data.append("calendar_event_ids[]", id));
  data.append("own_values", ownValues);
  if (ownValues === "choose") {
    Object.entries(chosen || {}).forEach(([eventId, keys]) => {
      keys.forEach((key) => data.append(`chosen[${eventId}][]`, key));
    });
  }
  return data;
};

export const resultSummary = (summary) => {
  if (!summary) return "";
  if (summary.updating === 0) return "0 dates will update. New dates you add will use these changes.";
  const bits = [`${summary.updating} ${plural(summary.updating, "date")} will update`];
  if (summary.keeping_chart > 0) bits.push(`${summary.keeping_chart} keep their chart`);
  if (summary.keeping_own_values > 0) bits.push(`${summary.keeping_own_values} keep their own values`);
  if (summary.staying_on_sale > 0) bits.push(`${summary.staying_on_sale} ${plural(summary.staying_on_sale, "stays", "stay")} on sale`);
  if (summary.keeping_current > 0) {
    bits.push(`${summary.keeping_current} ${plural(summary.keeping_current, "keeps its current value", "keep their current values")}`);
  }
  return bits.join(" · ");
};

export const UPDATING_STATUSES = ["updates", "updates_clears_own_value", "updates_keeps_chart"];

export const RESULT_COLORS = {
  past: "gray",
  not_included: "gray",
  updates_keeps_chart: "orange",
  keeps_chart: "warning",
  keeps_own_value: "warning",
  stays_on_sale: "warning",
  keeps_current: "warning",
  not_affected: "gray",
  updates_clears_own_value: "orange",
  updates: "brand",
};
